import { keyframes } from "@emotion/react";
import { Box, Grid, GridColumn, Pagination } from "@modernatx-internal/component-library";
import React from "react";

import { ContentComponentProps, ContentStripContent } from "@/content-V2/types";

const animateInBack = keyframes({
  from: {
    opacity: 0,
    transform: "translateX(-10%)"
  },
  to: {
    opacity: 1,
    transform: "translateX(0)"
  }
});

const animateInForward = keyframes({
  from: {
    opacity: 0,
    transform: "translateX(10%)"
  },
  to: {
    opacity: 1,
    transform: "translateX(0)"
  }
});

export const ContentStrip: React.FC<ContentComponentProps<ContentStripContent>> = ({
  children
}) => {
  const [direction, setDirection] = React.useState<"back" | "forward">("forward");
  const [initial, setInitial] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const pointerStart = React.useRef(0);
  const pointerTriggered = React.useRef(false);
  const contentItems = React.Children.toArray(children).filter(React.isValidElement);
  const count = contentItems.length;
  const contentItemNext = contentItems[page - 1] ?? null;

  const handlePageChange = React.useCallback(
    (pageNext: number, directionNext: "back" | "forward") => {
      if (!count) {
        return;
      }

      setDirection(directionNext);
      setInitial(false);
      setPage(pageNext > count ? 1 : pageNext < 1 ? count : pageNext);
    },
    [count]
  );

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    pointerStart.current = e.clientX;
    pointerTriggered.current = false;
  }, []);

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== "mouse" && !pointerTriggered.current) {
        const touchDistance = e.clientX - pointerStart.current;

        if (touchDistance >= 40) {
          pointerTriggered.current = true;
          handlePageChange(page - 1, "back");
        } else if (touchDistance <= -40) {
          pointerTriggered.current = true;
          handlePageChange(page + 1, "forward");
        }
      }
    },
    [handlePageChange, page]
  );

  if (!count) {
    return null;
  }

  return (
    <Box sx={{ overflow: "hidden", pb: 6, pt: 6 }}>
      <Grid>
        <GridColumn
          data-testid="content-strip"
          sx={{ touchAction: "pan-y" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
        >
          <Box
            key={page}
            data-testid={`content-item-${page}`}
            sx={{
              animation: initial
                ? null
                : `${
                    direction === "forward" ? animateInForward : animateInBack
                  } 400ms cubic-bezier(0.4, 0.005, 0.06, 1)`
            }}
          >
            {contentItemNext}
          </Box>
          {count > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 5 }}>
              <Pagination
                continuous={true}
                count={count}
                hideNextPrevButtons={[true, true, false]}
                page={page}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
        </GridColumn>
      </Grid>
    </Box>
  );
};
